'use client'

import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon } from '@hugeicons-pro/core-stroke-rounded'
import type { IconSvgElement } from '@hugeicons/react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  AUTOMATION_CHANNEL_META,
  type AutomationChannel,
  type CommandIconKey,
} from '@/lib/automations/fallback-data'
import { CHANNEL_ICON, COMMAND_ICON, COMMAND_ICON_KEYS } from '@/lib/automations/command-appearance'
import { cn } from '@/lib/utils'

const iconKeys = COMMAND_ICON_KEYS
const channelKeys = Object.keys(AUTOMATION_CHANNEL_META) as AutomationChannel[]

type CommandAppearancePickerProps = {
  iconKey: CommandIconKey
  channel: AutomationChannel
  onIconChange: (iconKey: CommandIconKey) => void
  onChannelChange: (channel: AutomationChannel) => void
}

export function CommandAppearancePicker({
  iconKey,
  channel,
  onIconChange,
  onChannelChange,
}: CommandAppearancePickerProps) {
  const channelMeta = AUTOMATION_CHANNEL_META[channel]

  return (
    <TooltipProvider>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg border border-atrium-border bg-atrium-background px-2 py-2 transition-colors hover:bg-atrium-content"
          >
            <span
              className="grid size-9 place-items-center rounded-lg text-white"
              style={{ background: channelMeta.gradient }}
            >
              <HugeiconsIcon icon={COMMAND_ICON[iconKey]} className="size-5" />
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-semibold"
              style={{ background: channelMeta.tint, color: channelMeta.ink }}
            >
              <HugeiconsIcon icon={CHANNEL_ICON[channel]} className="size-3" />
              {channelMeta.label}
            </span>
            <HugeiconsIcon icon={ArrowDown01Icon} className="size-4 text-atrium-muted" />
          </button>
        </PopoverTrigger>

        {/* Two-column layout: Channel (left) + Icon (right). Channels render
            as a row of color circles with the label in a tooltip. Icons
            keep their grid layout but tighter. */}
        <PopoverContent align="start" className="w-[24rem] p-3">
          <div className="grid grid-cols-2 gap-4">
            {/* Channel column */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-atrium-muted">Channel</p>
              <div className="flex flex-wrap gap-1.5">
                {channelKeys.map((key) => {
                  const meta = AUTOMATION_CHANNEL_META[key]
                  const selected = channel === key
                  return (
                    <Tooltip key={key}>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          aria-label={meta.label}
                          aria-pressed={selected}
                          onClick={() => onChannelChange(key)}
                          className={cn(
                            'grid size-8 shrink-0 place-items-center rounded-full text-white transition-all',
                            selected
                              ? 'ring-2 ring-offset-2 ring-offset-atrium-background scale-105'
                              : 'opacity-80 hover:opacity-100 hover:scale-105',
                          )}
                          style={{
                            background: meta.gradient,
                            // Ring color picks up the channel ink so the
                            // selected state visually anchors to the chip
                            // colour below.
                            ['--tw-ring-color' as string]: meta.ink,
                          }}
                        >
                          <HugeiconsIcon icon={CHANNEL_ICON[key]} className="size-3.5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{meta.label}</TooltipContent>
                    </Tooltip>
                  )
                })}
              </div>
            </div>

            {/* Icon column */}
            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-atrium-muted">Icon</p>
              <div className="grid grid-cols-4 gap-1.5">
                {iconKeys.map((key) => {
                  const selected = iconKey === key
                  return (
                    <button
                      key={key}
                      type="button"
                      aria-pressed={selected}
                      className={cn(
                        'grid size-8 place-items-center rounded-md border border-atrium-border transition-colors hover:bg-atrium-content',
                        selected && 'ring-2 ring-offset-1 ring-offset-atrium-background',
                      )}
                      style={selected ? { background: channelMeta.tint, borderColor: channelMeta.ink, ['--tw-ring-color' as string]: channelMeta.ink } : undefined}
                      onClick={() => onIconChange(key)}
                    >
                      <HugeiconsIcon
                        icon={COMMAND_ICON[key] as IconSvgElement}
                        className="size-4"
                        style={selected ? { color: channelMeta.ink } : undefined}
                      />
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}
